import { Box, useTheme } from '@material-ui/core';
import { ResponsiveLine } from '@nivo/line';
import * as d3 from 'd3';
import * as React from 'react';
import { useEffect } from 'react';
import AnalysisChartLoading from '../../AnalysisChartLoading';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { ChartProps } from '../../AnalysisList';
import { internationalText } from '../../internationalText';
import DataTimedOut, { didQueryTimeout } from '../DataTimedOut';
import NotEnoughData from '../NotEnoughData';

export interface MonthlyDemandProps extends ChartProps {}

const MonthlyDemandChart: React.FC<MonthlyDemandProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showSerpOnly: true,
          showTagGroupId: true,
          showTagId: true,
        },
      })),
    []
  );
  // Data fetching
  const { result, query } = projectsApiService.queries.analysis.useGetMonthlyDemand({
    projectId: project.id,
    serpOnly: analysisFilterState?.serpOnly,
    tagGroupId: analysisFilterState?.tagGroupId,
    tagId: analysisFilterState?.tagId,
  });

  // Styling
  const theme = useTheme();
  const chartTheme = {
    theme: {
      fontSize: 14,
      color: theme.palette.info.main,
      labels: {
        text: {
          fontSize: 14,
          color: theme.palette.info.main,
        },
      },
    },
  };

  // Render
  if (didQueryTimeout(query)) {
    return <DataTimedOut />;
  }

  if (query?.loading) {
    return <AnalysisChartLoading />;
  }

  if (!result?.volumeSumPerMonth.length) {
    return <NotEnoughData />;
  }

  return (
    <div data-screenshot-id="chart-loaded">
      <div className="h-[500px]">
        <ResponsiveLine
          theme={chartTheme.theme}
          data={[
            {
              id: 'market-demand',
              data: [
                {
                  x: internationalText.chart.demand_by_month.month_1[language],
                  y: result?.volumeSumPerMonth.find((x) => x.month == 1)?.volume ?? 0,
                },
                {
                  x: internationalText.chart.demand_by_month.month_2[language],
                  y: result?.volumeSumPerMonth.find((x) => x.month == 2)?.volume ?? 0,
                },
                {
                  x: internationalText.chart.demand_by_month.month_3[language],
                  y: result?.volumeSumPerMonth.find((x) => x.month == 3)?.volume ?? 0,
                },
                {
                  x: internationalText.chart.demand_by_month.month_4[language],
                  y: result?.volumeSumPerMonth.find((x) => x.month == 4)?.volume ?? 0,
                },
                {
                  x: internationalText.chart.demand_by_month.month_5[language],
                  y: result?.volumeSumPerMonth.find((x) => x.month == 5)?.volume ?? 0,
                },
                {
                  x: internationalText.chart.demand_by_month.month_6[language],
                  y: result?.volumeSumPerMonth.find((x) => x.month == 6)?.volume ?? 0,
                },
                {
                  x: internationalText.chart.demand_by_month.month_7[language],
                  y: result?.volumeSumPerMonth.find((x) => x.month == 7)?.volume ?? 0,
                },
                {
                  x: internationalText.chart.demand_by_month.month_8[language],
                  y: result?.volumeSumPerMonth.find((x) => x.month == 8)?.volume ?? 0,
                },
                {
                  x: internationalText.chart.demand_by_month.month_9[language],
                  y: result?.volumeSumPerMonth.find((x) => x.month == 9)?.volume ?? 0,
                },
                {
                  x: internationalText.chart.demand_by_month.month_10[language],
                  y: result?.volumeSumPerMonth.find((x) => x.month == 10)?.volume ?? 0,
                },
                {
                  x: internationalText.chart.demand_by_month.month_11[language],
                  y: result?.volumeSumPerMonth.find((x) => x.month == 11)?.volume ?? 0,
                },
                {
                  x: internationalText.chart.demand_by_month.month_12[language],
                  y: result?.volumeSumPerMonth.find((x) => x.month == 12)?.volume ?? 0,
                },
              ],
            },
          ]}
          margin={{ top: 50, right: 50, bottom: 50, left: 50 }}
          xScale={{ type: 'point' }}
          axisBottom={{
            //orient: 'bottom',
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 0,
          }}
          axisLeft={{
            format: '~s',
            //orient: 'right',
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 0,
          }}
          colors={[palette.tertiaryScale[1]]}
          pointColor={palette.tertiaryScale[1]}
          pointSize={12}
          pointLabel={function (d: any) {
            return d3.format('.2s')(d.y);
          }}
          pointLabelYOffset={-24}
          lineWidth={3}
          animate={false}
          curve={'monotoneX'}
          enablePointLabel={true}
          isInteractive={false}
          //enableGridX={false}
          //enableGridY={false}
        />
      </div>
    </div>
  );
};

export default MonthlyDemandChart;
